// @ts-strict-ignore
import React, { useCallback, useMemo } from 'react';
import { AccordionSection } from './AccordionSection';
import { AccordionItem } from './AccordionItem';
import { AccordionContext, AccordionContextPayload } from './AccordionContext';

type AccordionProps = {
  onChange?: (event: { title: string; index: number; expanded: number[] }) => void;
  onSectionExpanded?: (event: { title: string; index: number }) => void;
  onSectionCollapsed?: (event: { title: string; index: number }) => void;
  expanded: number[];
};

export const BaseAccordion: React.FCWithChildren<AccordionProps> = ({
  children,
  expanded = [],
  onChange,
  onSectionExpanded,
  onSectionCollapsed,
}) => {
  const toggleHandler = useCallback(({ title, index }) => {
    const currentlyExpanded = expanded.indexOf(index) > -1 || false;
    let newExpanded = [...expanded];

    if (currentlyExpanded === false) {
      newExpanded.push(index);
      if (onSectionExpanded) {
        onSectionExpanded({
          title,
          index,
        });
      }
    } else {
      newExpanded = expanded.filter(idx => idx !== index);
      if (onSectionCollapsed) {
        onSectionCollapsed({
          title,
          index,
        });
      }
    }

    if (onChange) {
      onChange({
        title,
        index,
        expanded: newExpanded,
      });
    }
  }, [
    expanded,
    onChange,
    onSectionCollapsed,
    onSectionExpanded,
  ]);

  const contextValue = useMemo<AccordionContextPayload>(() => ({
    onToggleSection: toggleHandler,
    isSectionToggled: idx => expanded.indexOf(idx) !== -1,
  }), [
    expanded,
    toggleHandler,
  ]);

  return (
    <AccordionContext.Provider value={contextValue}>
      {children}
    </AccordionContext.Provider>
  );
};

export const Accordion = Object.assign(BaseAccordion, {
  Section: AccordionSection,
  Item: AccordionItem,
});

export default Accordion;
